import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { getPaperTypes, getPrinters, PaperType, Printer } from '@/services/productService';
import { Loader2 } from 'lucide-react';

// This should align with the backend's OrderItemCreateInput, but without orderId/uploadedFileId
export interface OrderItemData {
  paperTypeId: string;
  printerId: string;
  width: number;
  height: number;
  colorMode: 'COLOR' | 'BLACK_WHITE';
}

interface OrderItemEditorProps {
  uploadedFileId: string; // To know which file we are configuring
  onSubmit: (itemData: OrderItemData) => void;
  onCancel: () => void;
}

export const OrderItemEditor: React.FC<OrderItemEditorProps> = ({ uploadedFileId: _uploadedFileId, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [paperTypes, setPaperTypes] = useState<PaperType[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<OrderItemData>>({
    colorMode: 'COLOR',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [fetchedPaperTypes, fetchedPrinters] = await Promise.all([
          getPaperTypes(),
          getPrinters(),
        ]);
        setPaperTypes(fetchedPaperTypes);
        setPrinters(fetchedPrinters);
        setError(null);
      } catch (err) {
        setError(t('order_editor.fetch_error', 'Failed to load product options.'));
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'width' || name === 'height' ? parseInt(value, 10) : value }));
  };

  const handleSelectChange = (name: keyof OrderItemData) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (formData.paperTypeId && formData.printerId && formData.width && formData.height) {
      onSubmit(formData as OrderItemData);
    } else {
      // This is a simple alert, a more robust solution like a toast notification would be better
      alert('Please fill all required fields.');
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardHeader>
          <CardTitle>{t('order_editor.loading_title', 'Loading Editor...')}</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center items-center p-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardHeader>
          <CardTitle className="text-destructive">{t('order_editor.error_title', 'Error')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-lg mx-auto">
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>{t('order_editor.title', 'Configure Your Print')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="paperTypeId">{t('order_editor.paper_type', 'Paper Type')}</Label>
            <Select name="paperTypeId" onValueChange={handleSelectChange('paperTypeId')} required>
              <SelectTrigger>
                <SelectValue placeholder={t('order_editor.select_paper', 'Select a paper')} />
              </SelectTrigger>
              <SelectContent>
                {paperTypes.map(paper => (
                  <SelectItem key={paper.id} value={paper.id}>{paper.name} ({paper.brand})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="printerId">{t('order_editor.printer', 'Printer')}</Label>
            <Select name="printerId" onValueChange={handleSelectChange('printerId')} required>
              <SelectTrigger>
                <SelectValue placeholder={t('order_editor.select_printer', 'Select a printer')} />
              </SelectTrigger>
              <SelectContent>
                {printers.map(printer => (
                  <SelectItem key={printer.id} value={printer.id}>{printer.name} ({printer.model})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="width">{t('order_editor.width', 'Width (mm)')}</Label>
              <Input id="width" name="width" type="number" placeholder="e.g., 210" onChange={handleInputChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="height">{t('order_editor.height', 'Height (mm)')}</Label>
              <Input id="height" name="height" type="number" placeholder="e.g., 297" onChange={handleInputChange} required />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('order_editor.color_mode', 'Color Mode')}</Label>
            <Select name="colorMode" defaultValue="COLOR" onValueChange={handleSelectChange('colorMode')} required>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="COLOR">{t('order_editor.color', 'Color')}</SelectItem>
                <SelectItem value="BLACK_WHITE">{t('order_editor.bw', 'Black & White')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel', 'Cancel')}</Button>
          <Button type="submit">{t('order_editor.submit', 'Add to Order')}</Button>
        </CardFooter>
      </form>
    </Card>
  );
};
